import React from 'react';

import { useBookingCheckout } from '@/booking-checkout';
import { SummarySectionContainer } from '@/booking/components/SummarySectionContainer';
import { useActiveBrandConfig } from '@/brand';
import { getFullLocalisedDateString } from '@/common/utils/date';
import { DataTable, DataTableRow } from '@/core/components';
import { CurrencyPreview } from '@/currency-preview';
import { useHotel } from '@/hotel';
import { useLocale, useTranslation } from '@/i18n';
import { useTheme } from '@/ui/theme';
import { BodySmall, Heading } from '@/ui/typography';

import { useBooking } from '../hooks';
import { BookingRequest } from '../interfaces';
import TaxTotals from './TaxTotals.component';

interface BookingBreakdownPrimaryTableProps extends BookingRequest {
  showCheckInTimes?: boolean;
  showTotalNightCount?: boolean;
}

/**
 * The core information about a booking, in table row format.
 */
export const BookingBreakdownPrimaryTable: React.FC<
  BookingBreakdownPrimaryTableProps
> = (props) => {
  const booking = useBooking(props);
  const hotel = useHotel(props.hotelSlug);
  const { t } = useTranslation('bookingSummary');

  const locale = useLocale();

  if (!booking || !hotel) {
    return null;
  }

  return (
    <DataTable>
      {!booking.firstRoomStay.mainGuestIsPaying ? (
        <DataTableRow
          label={t('labels.mainGuest')}
          value={booking.firstRoomStay.mainGuestProfile.fullName}
          testId="main-guest-name"
        />
      ) : null}
      <DataTableRow
        label={t('labels.where')}
        value={hotel.name}
        testId="hotel-name"
      />
      <DataTableRow
        label={t('labels.checkInDate')}
        value={getFullLocalisedDateString(booking.firstRoomStay.from, locale)}
        testId="check-in-date"
      />
      <DataTableRow
        label={t('labels.checkOutDate')}
        value={getFullLocalisedDateString(booking.firstRoomStay.to, locale)}
        testId="check-out-date"
      />

      {props.showTotalNightCount && (
        <DataTableRow
          label={t('labels.nightCount')}
          value={booking.totalNumberOfNights}
          testId="night-count"
        />
      )}

      {/* If this booking contains a bundled membership, show the room total in the primary view.
          https://ennismore.atlassian.net/browse/BP-3484
          This is so we can communicate the discount when accounting for taxes. */}
      {booking.totalDiscount.membershipRateCode && (
        <DataTableRow
          label={t('labels.roomTotal')}
          value={
            <CurrencyPreview
              amount={booking.firstRoomStay.firstRatePlan.chargeBreakdown.getDisplayableTotalRoomRate(
                hotel.legacyTaxes.isAdvanced
              )}
            />
          }
          discount={
            <CurrencyPreview
              amount={booking.totalDiscount.amountBeforeDiscountAfterTax}
            />
          }
          testId="membership-room-total"
        />
      )}

      {props.showCheckInTimes && (
        <>
          <DataTableRow
            label={t('labels.checkInTime')}
            value={t('values.checkInTime', {
              time: booking.checkInAfter ?? hotel.operational.checkIn,
            })}
            testId="check-in-time"
          />
          <DataTableRow
            label={t('labels.checkOutTime')}
            value={t('values.checkOutTime', {
              time: booking.checkOutBefore ?? hotel.operational.checkOut,
            })}
            testId="check-out-time"
          />
        </>
      )}

      {booking.dog && (
        <DataTableRow testId="dog" label={t('labels.dogs')} value="1 Dog" />
      )}
      {booking.childBedTypeText && (
        <DataTableRow
          label={t('labels.childBed')}
          value={`1 ${booking.childBedTypeText}`}
          testId="child-bed"
        />
      )}
    </DataTable>
  );
};

interface BookingBreakdownRoomsTableProps extends BookingRequest {
  separator?: React.ReactNode;
  pricePerNightMode: 'actual' | 'average';
  displayConfirmationNumber: boolean;
  wrapElementsInContainer: boolean;
  className?: string;
}

/**
 * A list of rooms attached to the given booking, grouped and headed by sequential room number.
 */
export const BookingBreakdownRoomsTable: React.FC<
  BookingBreakdownRoomsTableProps
> = (props) => {
  const booking = useBooking(props);
  const hotel = useHotel(props.hotelSlug);
  const {
    config: { additionalInfoFields },
  } = useBookingCheckout(props.bookingId);
  const { t } = useTranslation('bookingSummary');
  const { t: getRoomOccupancyTypeTranslation } =
    useTranslation('roomOccupancyTypes');
  const { componentProperties } = useTheme();

  const { showRoomTotalInBreakdown } = useActiveBrandConfig();

  if (!booking || !hotel) {
    return null;
  }

  // Either comes from the booking, or if we haven't checked out yet, grab this from the local additional info form
  const isSpecialAssistanceRequested: boolean =
    booking.isSpecialAssistanceRequested ||
    Boolean(additionalInfoFields?.accessRequirements);

  const isMultiRoomBooking = booking.roomStay.length > 1;
  const Container = props.wrapElementsInContainer
    ? SummarySectionContainer
    : React.Fragment;
  return (
    <>
      {booking.roomStay.map((stay, index) => (
        <React.Fragment key={index}>
          <Container>
            <div className={props.className}>
              {isMultiRoomBooking ? (
                <Heading
                  as="h6"
                  style="heading6"
                  css={{
                    paddingBottom: '24px',
                    ...componentProperties.staySummary?.roomNumberHeading,
                  }}
                >
                  {t('labels.room.roomNumber', { number: index + 1 })}
                </Heading>
              ) : null}
              <DataTable key={stay.operaId}>
                {/* If this booking has more than one room, render the PMS number for each room. */}
                {props.displayConfirmationNumber && isMultiRoomBooking ? (
                  <DataTableRow
                    label={t('labels.room.confirmationNumber')}
                    value={stay.operaId}
                    testId="confirmation-number"
                  />
                ) : undefined}
                <DataTableRow
                  label={t('labels.room.guests')}
                  value={
                    stay.roomGuestTypeLabelTranslationKey
                      ? getRoomOccupancyTypeTranslation(
                          stay.roomGuestTypeLabelTranslationKey
                        )
                      : undefined
                  }
                  testId="guest-occupancy"
                />
                <DataTableRow
                  label={t('labels.room.roomType')}
                  value={stay.firstRoomDetail.name}
                  testId="room-type"
                />
                {stay.firstRatePlan.rateCode === 'BAR' ? undefined : (
                  <DataTableRow
                    label={t('labels.room.rateCode')}
                    value={stay.firstRatePlan.rateTitle}
                    testId="room-rate-code"
                  />
                )}

                {showRoomTotalInBreakdown && (
                  <>
                    <DataTableRow
                      label={t('labels.room.subtotal')}
                      testId="room-subtotal"
                      value={
                        <CurrencyPreview
                          amount={
                            stay.firstRatePlan.chargeBreakdown.totalBeforeTax
                          }
                        />
                      }
                    />
                  </>
                )}
                {isSpecialAssistanceRequested && (
                  <>
                    <DataTableRow
                      label={t('labels.room.accessibleRoom')}
                      value={t('values.confirmed')}
                      testId="accessible-room"
                    />
                  </>
                )}
                {stay.firstRoomDetail.addOnsDetail?.map((addOn) => (
                  <DataTableRow
                    key={addOn.id}
                    testId={`addon-${addOn.id}`}
                    label={
                      addOn.calculatedQuantity > 1
                        ? `${addOn.name} (x${addOn.calculatedQuantity})`
                        : addOn.name
                    }
                    value={
                      <CurrencyPreview amount={addOn.cost.amountAfterTax} />
                    }
                  />
                ))}
              </DataTable>
            </div>
          </Container>
          {index < booking.roomStay.length - 1 && props.separator}
        </React.Fragment>
      ))}
    </>
  );
};

/**
 * Lists all taxes associated with the booking, in table row format.
 */
export const BookingBreakdownTaxTotalsTable = (props: BookingRequest) => {
  const booking = useBooking(props);
  const hotel = useHotel(props.hotelSlug);
  const { showTaxTotalInBreakdown } = useActiveBrandConfig();
  const { t: getTaxTranslation } = useTranslation('taxes');
  const { t: getTaxHelpTranslation } = useTranslation('taxesHelp');
  const {
    componentProperties: { staySummary },
  } = useTheme();

  if (!showTaxTotalInBreakdown || !booking || !hotel) {
    return null;
  }

  if (booking.firstTotalChargeBreakdown.costBreakdown?.taxesFees.length === 0) {
    return (
      <BodySmall css={staySummary?.dataRow?.label}>
        {getTaxTranslation('noTaxes')}
      </BodySmall>
    );
  }

  return (
    <TaxTotals
      taxes={
        booking.firstTotalChargeBreakdown.costBreakdown?.taxesFees.map(
          (item) => ({
            name:
              item.name ||
              getTaxTranslation(item.humanTaxNameKey || 'otherTaxes'),
            amount: item.amount,
            // If a description is present, render that.
            // If not, attempt to resolve a translation string matching the code.
            helpText: item.description?.length
              ? item.description
              : item.helpTextKey
              ? getTaxHelpTranslation(item.helpTextKey)
              : item.name === 'Destination Fee' &&
                hotel.destinationFeeDescription
              ? hotel.destinationFeeDescription
              : '',
          })
        ) || []
      }
    />
  );
};

/**
 * A table data row containing the overall total for the booking.
 */
export const BookingBreakdownStayTotalsTable = (props: BookingRequest) => {
  const booking = useBooking(props);
  const hotel = useHotel(props.hotelSlug);
  const { t } = useTranslation('bookingSummary');

  if (!booking || !hotel) {
    return null;
  }

  return (
    <>
      <DataTableRow
        label={t('labels.total')}
        value={
          <CurrencyPreview
            amount={booking.firstTotalChargeBreakdown.grandTotal}
          />
        }
        testId="stay-total"
      />
    </>
  );
};
